import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { routerActions } from 'connected-react-router';
import * as alertActions from '../alerts/actions';
import * as userActions from './actions';
import * as usersApi from './api';
import { disableUserSuccess } from './actions';
import { canExecute } from '../utils/canExecute';
import { checkTokenExpiration } from '../utils/checkTokenExpiration';
import { Alert } from '../alerts';
import {
    invite, getLockedUsers,
} from '../../lib/authentication';
import {
    fetch as fetchInvites
} from './../invites/actions';

function* cancelInvitation(action) {
    const { user, currentUser } = action.value;
    try {
        if (user.status !== 'pending') {
            throw new Error(
                'Unable to cancel invitation since the user is not in a pending status'
            );
        }
        yield call(checkTokenExpiration, currentUser);
        yield call(canExecute, currentUser, 'brokers:write');
        yield call(usersApi.deleteUser, user.id);
        yield put(userActions.cancelInvitationSuccess(user, currentUser));
        yield put(
            alertActions.setAlert(new Alert('success', `Invitation for ${user.username} cancelled`))
        );
        yield put(userActions.fetchUsers(currentUser));
    } catch (e) {
        yield put(userActions.cancelInvitationFailure(user, e));
        yield put(
            alertActions.setAlert(
                new Alert('danger', `Could not cancel invitation for ${user.username}`)
            )
        );
    }
}

function* revokeUser(action) {
    const { user, currentUser } = action.value;
    try {
        yield call(checkTokenExpiration, currentUser);
        yield call(usersApi.updateStatus, user.id, 'revoked');
        yield put(userActions.revokeUserSuccess(user));
        yield put(
            alertActions.setAlert(
                new Alert(
                    'warning',
                    `Account revoked for: ${
                    user.username
                    }. You will still be able to access their submissions.`
                )
            )
        );
        yield put(userActions.fetchUsers(currentUser));
    } catch (e) {
        yield put(userActions.revokeUserFailure(user, e));
        yield put(
            alertActions.setAlert(new Alert('danger', `Could not revoke user: ${user.username}.`))
        );
    }
}

function* disableUser(action) {
    const { user, currentUser } = action.value;
    try {
        yield call(checkTokenExpiration, currentUser);
        yield call(usersApi.updateStatus, user.id, 'disabled');
        yield put(userActions.disableUserSuccess(user));
        yield put(
            alertActions.setAlert(
                new Alert(
                    'success',
                    `Account disabled for: ${
                    user.username
                    }. You will still be able to access their submissions.`
                )
            )
        );
        yield put(userActions.fetchUsers(currentUser));
    } catch (e) {
        yield put(userActions.disableUserFailure(user, e));
        yield put(
            alertActions.setAlert(new Alert('danger', `Could not disable user: ${user.username}.`))
        );
    }
}

function* enableUser(action) {
    const { user, currentUser } = action.value;
    try {
        yield call(checkTokenExpiration, currentUser);
        yield call(usersApi.updateStatus, user.id, 'active');
        yield put(userActions.enableUserSuccess(user));
        yield put(
            alertActions.setAlert(
                new Alert(
                    'success',
                    `Account reactivated for: ${
                    user.username
                    }. The user can now log in with their existing credentials.`
                )
            )
        );
        yield put(userActions.fetchUsers(currentUser));
    } catch (e) {
        yield put(userActions.enableUserFailure(user, e));
        yield put(
            alertActions.setAlert(new Alert('danger', `Could not enable user: ${user.username}.`))
        );
    }
}

function* resendInvitation(action) {
    const { user, currentUser } = action.value;
    try {
        yield call(checkTokenExpiration, currentUser);
        yield call(canExecute, currentUser, 'brokers:write');
        yield call(invite, user.username, currentUser.email, user.role, user.brokerId);

        yield put(userActions.resendInvitationSuccess(user, currentUser));
        yield put(
            alertActions.setAlert(
                new Alert('success', `The invitation was emailed to ${user.username}`)
            )
        );
        yield put(userActions.fetchUsers(currentUser));
        yield put(fetchInvites({
            brokerId: user.brokerId,
        }));
    } catch (e) {
        yield put(userActions.resendInvitationFailure(user, e));
        yield put(alertActions.setAlert(new Alert('danger', e.errorSummary)));
    }
}

export function* fetchLockedUsers() {
    const lockedUsers = yield call(getLockedUsers);
    yield put(userActions.fetchLockedUsersSuccess(lockedUsers.data.data.data))
}

export function* fetchUsers(action) {
    try {
        const users = yield call(usersApi.fetchAll);
        yield put(userActions.fetchUsersSuccess(users));
    } catch (e) {
        yield put(userActions.fetchUsersFailure(e));
    }
}

export function* inviteUser(action) {
    const { email, isAdmin, userType, brokerageId, currentUser } = action.value;
    let invitation = { email, isAdmin, userType, brokerageId, currentUser };
    try {
        const userRole = `${userType}${isAdmin === 'true' ? ':admin' : ''}`;
        yield call(canExecute, currentUser, 'brokers:write');
        yield call(invite, email, currentUser.email, userRole, brokerageId);
        yield put(alertActions.setAlert(
            new Alert('success', `Success! An invite as been emailed to ${email}`)
        ));
        yield put(userActions.changeBrokerageSuccess(currentUser));
        yield put(userActions.fetchUsers(currentUser));
        yield put(fetchInvites({
            brokerId: brokerageId
        }));
    } catch (e) {
        yield put(userActions.inviteUserFailure(e, invitation));
        const errorCode = ["InviteExistsBrokerError", "UserExistBrokerError"];

        if (!errorCode.includes(e.errorCode)) {
            const message = e.errorSummary ? e.errorSummary : 'Failure! An Invite has not been sent.'
            yield put(alertActions.setAlert(new Alert('danger', message)));
        }
    }
}

function* locationChange(action) {
    yield put(alertActions.removeAllAlerts());
    yield put(userActions.clearInvitation());
}

function* saveUser(action) {
    const { user, currentUser } = action.value;
    try {
        const { id, firstName, lastName, title, phone, phoneExt, role } = user;
        yield call(checkTokenExpiration, currentUser);
        yield call(canExecute, currentUser, 'brokers:write');
        yield call(usersApi.saveUser, id, firstName, lastName, title, phone, phoneExt, role);
        yield put(userActions.saveUserSuccess(user));
        yield put(
            alertActions.setAlert(
                new Alert('success', `The profile for ${user.username} has been updated`)
            )
        );
        yield put(userActions.fetchUsers(currentUser));
    } catch (e) {
        yield put(userActions.saveUserFailure(user, e));
        yield put(
            alertActions.setAlert(
                new Alert(
                    'danger',
                    `There was an error editing the user (${
                    user.username
                    }). Please contact support!`
                )
            )
        );
    }
}

export function* changeBrokerage(action) {
    yield put(alertActions.removeAllAlerts());
    const { newBrokerage, user, currentUser } = action.value;
    try {
        yield call(canExecute, currentUser, 'brokers:write');
        const { brokerId, name } = newBrokerage;
        const { id } = user;
        if (id) {
            yield call(usersApi.updateBrokerage, id, brokerId);
        } else {
            yield call(invite, user.email, currentUser.email, 'broker:admin', newBrokerage.brokerId, true);
        }
        yield put(userActions.changeBrokerageSuccess(user));
        yield put(
            alertActions.setAlert(
                new Alert(
                    'success',
                    `The user account (${
                    user.username || user.email
                    }) has been moved to <a href='./${brokerId}'>${name}</a> `
                )
            )
        );
        yield put(fetchInvites({
            brokerId: brokerId
        }));
        yield put(userActions.fetchUsers(currentUser));
    } catch (e) {
        yield put(userActions.changeBrokerageFailure(user, e));
        yield put(
            alertActions.setAlert(
                new Alert(
                    'danger',
                    `There was an error changing the brokerage of the user (${
                    user.username || user.email
                    }). Please contact support!`
                )
            )
        );
    }
}
export function* signupUser(action) {
    const { type, value: email } = action;
    try {
        const status = yield call(usersApi.signupUser, email);
        if (status !== 'REQUEST_ACCESS') {
            yield put(userActions.signupUserSuccess(email));
            yield call(routerActions.push, `signup-success?email=${encodeURIComponent(email)}`);
        } else {
            yield put(userActions.signupUserExist(email));
            yield call(routerActions.push, `request-access?email=${encodeURIComponent(email)}`);
        }
    } catch (e) {
        yield put(userActions.signupUserFailed(email, e));
        yield call(console.error, e);
        yield call(routerActions.push, `request-access?email=${encodeURIComponent(email)}`);
    }
}

export function* sendRequestAccess(action) {
    const { type, value: userData } = action;
    try {
        yield call(usersApi.requestAccess, userData);
        yield put(userActions.sendRequestAccessSuccess(userData.email));
    } catch (e) {
        yield put(userActions.sendRequestAccessFailed(e));
    }
}

function* watchForCancelInvitation() {
    yield takeEvery(userActions.CANCEL_INVITATION, cancelInvitation);
}

function* watchForRevokeUser() {
    yield takeEvery(userActions.REVOKE_USER, revokeUser);
}

function* watchForDisableUser() {
    yield takeEvery(userActions.DISABLE_USER, disableUser);
}

function* watchForEnableUser() {
    yield takeEvery(userActions.ENABLE_USER, enableUser);
}

function* watchForFetchUsers() {
    yield takeEvery(userActions.FETCH_USERS, fetchUsers);
}

function* watchForInviteUser() {
    yield takeEvery(userActions.INVITE_USER, inviteUser);
}

function* watchForLocationChange() {
    yield takeEvery('@@router/LOCATION_CHANGE', locationChange);
}

function* watchForResendInvitation() {
    yield takeEvery(userActions.RESEND_INVITATION, resendInvitation);
}

function* watchForFetchLockedUsers() {
    yield takeEvery(userActions.FETCH_LOCKED_USERS, fetchLockedUsers);
}

function* watchForSaveUser() {
    yield takeEvery(userActions.SAVE_USER, saveUser);
}

export function* watchForChangeBrokerage() {
    yield takeEvery(userActions.CHANGE_BROKERAGE, changeBrokerage);
}

function* watchForSignupUser() {
    yield takeEvery(userActions.SIGNUP_USER, signupUser);
}

function* watchForSendRequestAccess() {
    yield takeEvery(userActions.SEND_REQUEST_ACCESS, sendRequestAccess);
}

export default function* root() {
    yield all([
        fork(watchForCancelInvitation),
        fork(watchForDisableUser),
        fork(watchForEnableUser),
        fork(watchForFetchUsers),
        fork(watchForInviteUser),
        fork(watchForLocationChange),
        fork(watchForResendInvitation),
        fork(watchForFetchLockedUsers),
        fork(watchForSaveUser),
        fork(watchForChangeBrokerage),
        fork(watchForRevokeUser),
        fork(watchForSignupUser),
        fork(watchForSendRequestAccess)
    ]);
}
